import { useState } from 'react';
import { hasPending, clearOutbox } from '../lib/offline/outbox.js';
import { clearSavedQueries } from '../lib/offline/persist.js';
import { Link, useNavigate } from 'react-router-dom';
import { useBack } from '../lib/back.js';
import { useQueryClient } from '@tanstack/react-query';
import { alpha, theme } from '../theme.js';
import { useAuth } from '../context/AuthContext.js';
import { signOut, updatePassword } from '../lib/auth.js';
import { passwordErrorMessage, PASSWORD_RULES } from '../lib/authErrors.js';
import { api } from '../lib/api.js';
import { emitError, emitSuccess } from '../lib/toastBus.js';
import { useMe, useUpdateMyName } from '../lib/queries.js';
import { PageShell } from '../components/PageShell.js';
import { Icon } from '../components/Icon.js';
import { InstallCard } from '../components/InstallCard.js';
import { NotificationsCard } from '../components/NotificationsCard.js';
import { QuietHoursCard } from '../components/QuietHoursCard.js';
import { CalendarCard } from '../components/CalendarCard.js';
import { AppearanceCard } from '../components/AppearanceCard.js';
import { MyUnitCard } from '../components/MyUnitCard.js';
import { MyCoverStatsCard } from '../components/MyCoverStatsCard.js';
import { forgetThisDevice } from '../lib/push.js';

export function ProfilePage() {
  const nav = useNavigate();
  const back = useBack('/');
  const { user, refresh } = useAuth();
  const qc = useQueryClient();

  const { data: me } = useMe();
  const renameMut = useUpdateMyName();
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState('');

  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  function handleSaveName() {
    // Failures are toasted by the global mutation handler.
    renameMut.mutate(nameDraft.trim(), { onSuccess: () => setEditingName(false) });
  }

  async function handleSignOut() {
    if (hasPending() && !window.confirm('Some check changes haven’t been sent yet (no signal). Signing out now loses them. Sign out anyway?')) return;
    await forgetThisDevice(); // the next person on this phone mustn't get my notifications
    await signOut();
    qc.clear(); // don't leak this user's cached data (e.g. /me) to the next sign-in
    await Promise.all([clearOutbox(), clearSavedQueries()]);
    await refresh();
    nav('/login', { replace: true });
  }

  async function handleDeleteAccount() {
    setDeleting(true);
    try {
      await api.delete('/me');
      await signOut().catch(() => {}); // Cognito user is already gone
      qc.clear();
      await Promise.all([clearOutbox(), clearSavedQueries()]);
      await refresh();
      nav('/login', { replace: true });
    } catch (e) {
      emitError(e instanceof Error ? e.message : 'Could not delete account');
      setDeleting(false);
      setConfirmDelete(false);
    }
  }

  return (
    <PageShell noNav>
      {/* header */}
      <div style={{
        padding: '12px 16px',
        background: theme.surface,
        borderBottom: `1px solid ${theme.border}`,
        display: 'flex', alignItems: 'center', gap: 10,
        position: 'sticky', top: 0, zIndex: 5,
      }}>
        <button onClick={back} style={iconBtn} aria-label="Back">
          <Icon name="back" size={18} />
        </button>
        <div style={{ fontSize: 17, fontWeight: 700, color: theme.text }}>Profile</div>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', background: theme.bg }}>
        {/* avatar + info */}
        <div style={{ padding: '32px 20px 24px', textAlign: 'center' }}>
          <div style={{
            width: 72, height: 72, borderRadius: 36,
            background: theme.surface2, border: `1px solid ${theme.border}`,
            margin: '0 auto 12px',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 24, fontWeight: 700, color: theme.text,
          }}>
            {(me?.displayName ?? user?.email)?.slice(0, 2).toUpperCase()}
          </div>
          {editingName ? (
            <form
              onSubmit={(e) => { e.preventDefault(); handleSaveName(); }}
              style={{ display: 'flex', gap: 8, justifyContent: 'center', marginBottom: 4 }}
            >
              <input
                autoFocus value={nameDraft} maxLength={100}
                onChange={(e) => setNameDraft(e.target.value)}
                style={{
                  flex: '0 1 220px', padding: '8px 10px', borderRadius: 8,
                  border: `1px solid ${theme.border}`, background: theme.surface,
                  color: theme.text, fontSize: 15, fontFamily: theme.font,
                }}
              />
              <button type="submit" disabled={renameMut.isPending || !nameDraft.trim()} style={smallBtn}>
                Save
              </button>
              <button type="button" onClick={() => setEditingName(false)} style={{ ...smallBtn, background: 'none' }}>
                Cancel
              </button>
            </form>
          ) : (
            <button
              onClick={() => { setNameDraft(me?.displayName ?? ''); setEditingName(true); }}
              disabled={!me}
              title="Edit name"
              style={{
                background: 'none', border: 'none', cursor: 'pointer', fontFamily: theme.font,
                fontSize: 16, fontWeight: 600, color: theme.text,
                display: 'inline-flex', alignItems: 'center', gap: 6,
              }}
            >
              {me?.displayName ?? '…'}
              <Icon name="edit" size={14} style={{ color: theme.textMuted }} />
            </button>
          )}
          <div style={{ fontSize: 13, color: theme.textMuted }}>{user?.email}</div>
          <div style={{
            marginTop: 8, display: 'inline-block',
            fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 999,
            background: theme.status.service.bg, color: theme.status.service.fg,
          }}>
            {me?.role === 'admin' ? 'ADMIN' : 'MEMBER'}
            {me?.team && ` · ${me.team.name}${me.team.role !== 'member' ? ` (${me.team.role.toUpperCase()})` : ''}`}
          </div>
        </div>

        {me?.unit && <MyUnitCard unit={me.unit} />}
        <MyCoverStatsCard />
        <InstallCard />
        <NotificationsCard />
        <QuietHoursCard />
        <CalendarCard />
        <AppearanceCard />

        {me?.systemAdmin && (
          <div style={{ padding: '0 16px 12px' }}>
            <button onClick={() => nav('/system')} style={{
              width: '100%', padding: '13px 16px', background: theme.surface, border: `1px solid ${theme.border}`,
              borderRadius: 12, fontSize: 15, fontWeight: 600, color: theme.text, cursor: 'pointer', fontFamily: theme.font,
              display: 'flex', alignItems: 'center', gap: 10,
            }}>
              <Icon name="shield" size={17} style={{ color: theme.accent }} />
              <span style={{ flex: 1, textAlign: 'left' }}>System admin</span>
              <Icon name="chevron" size={16} style={{ color: theme.textMuted }} />
            </button>
          </div>
        )}

        <div style={{ padding: '0 16px 12px' }}>
          <button onClick={() => nav('/help')} style={{
            width: '100%', padding: '13px 16px', background: theme.surface, border: `1px solid ${theme.border}`,
            borderRadius: 12, fontSize: 15, fontWeight: 600, color: theme.text, cursor: 'pointer', fontFamily: theme.font,
            display: 'flex', alignItems: 'center', gap: 10,
          }}>
            <Icon name="help" size={17} style={{ color: theme.accent }} />
            <span style={{ flex: 1, textAlign: 'left' }}>Help and how-to</span>
            <Icon name="chevron" size={16} style={{ color: theme.textMuted }} />
          </button>
        </div>

        <ChangePasswordCard />

        {/* sign out */}
        <div style={{ padding: '0 16px' }}>
          <button
            onClick={handleSignOut}
            style={{
              width: '100%', padding: '13px 0',
              background: theme.surface,
              border: `1px solid ${theme.border}`,
              borderRadius: 12, fontSize: 15, fontWeight: 600,
              color: theme.status.danger.fg, cursor: 'pointer',
              fontFamily: theme.font,
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            }}
          >
            <Icon name="logout" size={17} style={{ color: theme.status.danger.dot }} />
            Sign out
          </button>
        </div>

        {/* delete account */}
        <div style={{ padding: '32px 16px 24px' }}>
          {!confirmDelete ? (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <button onClick={() => setConfirmDelete(true)} style={{ ...deleteLink, width: 'auto' }}>
                Delete my account
              </button>
              <Link to="/privacy" style={{ fontSize: 13, color: theme.textMuted, fontFamily: theme.font }}>Privacy policy</Link>
            </div>
          ) : (
            <div style={{
              padding: 14, borderRadius: 12,
              background: theme.status.danger.bg,
              border: `1px solid ${alpha(theme.status.danger.dot, 20)}`,
            }}>
              <div style={{ fontSize: 13, color: theme.status.danger.fg, lineHeight: 1.45, marginBottom: 12 }}>
                This permanently deletes your login and removes you from every unit and team you're in.
                Inspections you did stay on record under your name. This can't be undone.
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  onClick={() => setConfirmDelete(false)}
                  disabled={deleting}
                  style={{ ...confirmBtn, background: theme.surface, color: theme.text, border: `1px solid ${theme.border}` }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteAccount}
                  disabled={deleting}
                  style={{ ...confirmBtn, background: theme.status.danger.dot, color: '#fff', border: 'none' }}
                >
                  {deleting ? 'Deleting…' : 'Delete account'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </PageShell>
  );
}

/** Change password while signed in: current + new + confirm. Stays signed in. */
function ChangePasswordCard() {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  function close() {
    setOpen(false);
    setCurrent(''); setNext(''); setConfirm(''); setError('');
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    if (!current || !next) { setError('Fill in your current and new password.'); return; }
    if (next !== confirm) { setError('The new passwords don’t match.'); return; }
    setSaving(true);
    try {
      await updatePassword({ oldPassword: current, newPassword: next });
      emitSuccess('Password changed');
      close();
    } catch (err: unknown) {
      setError(passwordErrorMessage(err, 'Could not change your password', {
        NotAuthorizedException: 'Your current password is wrong.',
      }));
    } finally {
      setSaving(false);
    }
  }

  if (!open) {
    return (
      <div style={{ padding: '0 16px 12px' }}>
        <button onClick={() => setOpen(true)} style={rowBtn}>
          <Icon name="shield" size={17} style={{ color: theme.textDim }} />
          <span style={{ flex: 1, textAlign: 'left' }}>Change password</span>
          <Icon name="chevron" size={16} style={{ color: theme.textMuted }} />
        </button>
      </div>
    );
  }

  return (
    <div style={{ padding: '0 16px 12px' }}>
      <form onSubmit={handleSubmit} style={{
        padding: 16, background: theme.surface, border: `1px solid ${theme.border}`, borderRadius: 12,
        display: 'flex', flexDirection: 'column', gap: 10,
      }}>
        <div style={{ fontSize: 15, fontWeight: 600, color: theme.text }}>Change password</div>
        <label style={fieldLabel}>
          Current password
          <input type="password" value={current} onChange={(e) => setCurrent(e.target.value)}
            autoComplete="current-password" autoFocus required style={fieldInput} />
        </label>
        <label style={fieldLabel}>
          New password
          <input type="password" value={next} onChange={(e) => setNext(e.target.value)}
            autoComplete="new-password" minLength={10} required style={fieldInput} />
          <span style={{ fontSize: 12, fontWeight: 400, color: theme.textMuted }}>{PASSWORD_RULES}</span>
        </label>
        <label style={fieldLabel}>
          Confirm new password
          <input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)}
            autoComplete="new-password" required style={fieldInput} />
        </label>
        {error && (
          <div style={{
            fontSize: 13, color: theme.status.danger.fg, background: theme.status.danger.bg,
            padding: '10px 14px', borderRadius: 8,
          }}>{error}</div>
        )}
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" onClick={close} disabled={saving}
            style={{ ...confirmBtn, background: theme.surface, color: theme.text, border: `1px solid ${theme.border}` }}>
            Cancel
          </button>
          <button type="submit" disabled={saving}
            style={{ ...confirmBtn, background: theme.accent, color: theme.accentInk, border: 'none' }}>
            {saving ? 'Saving…' : 'Change password'}
          </button>
        </div>
      </form>
    </div>
  );
}

const rowBtn: React.CSSProperties = {
  width: '100%', padding: '13px 16px', background: theme.surface, border: `1px solid ${theme.border}`,
  borderRadius: 12, fontSize: 15, fontWeight: 600, color: theme.text, cursor: 'pointer', fontFamily: theme.font,
  display: 'flex', alignItems: 'center', gap: 10,
};

const fieldLabel: React.CSSProperties = {
  display: 'flex', flexDirection: 'column', gap: 6,
  fontSize: 13, fontWeight: 500, color: theme.textDim,
};

const fieldInput: React.CSSProperties = {
  height: 42, padding: '0 12px', borderRadius: 10,
  border: `1px solid ${theme.border}`, background: theme.bg,
  color: theme.text, fontSize: 15, fontFamily: theme.font, outline: 'none',
};

const smallBtn: React.CSSProperties = {
  padding: '8px 12px', borderRadius: 8, border: `1px solid ${theme.border}`,
  background: theme.surface2, color: theme.text, fontSize: 13, fontWeight: 600,
  cursor: 'pointer', fontFamily: theme.font,
};

const deleteLink: React.CSSProperties = {
  width: '100%', padding: '10px 0', background: 'none', border: 'none',
  fontSize: 13, fontWeight: 500, color: theme.textMuted, cursor: 'pointer',
  fontFamily: theme.font, textDecoration: 'underline',
};

const confirmBtn: React.CSSProperties = {
  flex: 1, padding: '11px 0', borderRadius: 10,
  fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: theme.font,
};

const iconBtn: React.CSSProperties = {
  width: 36, height: 36, border: `1px solid ${theme.border}`,
  background: theme.surface2, color: theme.text, borderRadius: 10,
  display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
};
